import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Send, CheckCircle, XCircle, Clock, Smartphone, AlertCircle, DollarSign,
  TrendingUp, Users, Phone, X 
} from 'lucide-react';
import { 
  initiateDisbursement, 
  checkMobileMoneyAccount, 
  getAllDisbursements,
  getDisbursementStatistics 
} from '../../services/admin/disbursementService';
import { getAllUsers } from '../../services/admin/userService';
import { toast } from '../../hooks/use-toast';
import PawaPayWeeklyReport from './PawaPayWeeklyReport';

const DisbursementsPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [disbursements, setDisbursements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [amount, setAmount] = useState('');
  const [provider, setProvider] = useState('airtel');
  const [notes, setNotes] = useState('');
  const [accountCheck, setAccountCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadDrivers(),
      loadDisbursements(),
      loadStats()
    ]);
    setLoading(false);
  };

  const loadDrivers = async () => {
    const result = await getAllUsers({ role: 'driver', limit: 1000 });
    if (result.success) {
      // Filter only verified drivers with pending earnings
      const eligible = result.data.users.filter(d => 
        d.verification_status === 'verified' && 
        (d.pending_earnings || 0) > 0
      );
      setDrivers(eligible);
    }
  };

  const loadDisbursements = async () => {
    const result = await getAllDisbursements({ limit: 50 });
    if (result.success) {
      setDisbursements(result.data.disbursements);
    }
  };

  const loadStats = async () => {
    const result = await getDisbursementStatistics(30);
    if (result.success) {
      setStats(result.data);
    }
  };

  const handleCheckAccount = async () => {
    if (!selectedDriver) return;
    
    setChecking(true);
    const result = await checkMobileMoneyAccount(
      selectedDriver.phone,
      provider
    );
    setChecking(false);

    if (result.success) {
      setAccountCheck(result.data);
      if (result.data.account_exists) {
        toast({ title: 'Account Verified ✓', description: `${result.data.account_name || 'Account'} is active` });
      } else {
        toast({ 
          title: 'Account Not Found', 
          description: 'Mobile money account not found or inactive', 
          variant: 'destructive' 
        });
      }
    } else {
      toast({ title: 'Check Failed', description: result.message, variant: 'destructive' });
    }
  };

  const handleDisburse = async () => {
    if (!selectedDriver || !amount) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast({ title: 'Error', description: 'Invalid amount', variant: 'destructive' });
      return;
    }

    const pendingEarnings = selectedDriver.pending_earnings || 0;
    if (amountNum > pendingEarnings) {
      toast({ 
        title: 'Insufficient Earnings', 
        description: `Driver has only K${pendingEarnings.toFixed(2)} pending`, 
        variant: 'destructive' 
      });
      return;
    }

    setProcessing(true);
    const result = await initiateDisbursement(
      selectedDriver.id,
      amountNum,
      provider,
      notes || null
    );
    setProcessing(false);

    if (result.success) {
      toast({ title: 'Success!', description: 'Disbursement initiated successfully' });
      setShowModal(false);
      resetModal();
      loadData();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const resetModal = () => {
    setSelectedDriver(null);
    setAmount('');
    setProvider('airtel');
    setNotes('');
    setAccountCheck(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      success: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
      processing: { color: 'bg-blue-100 text-blue-800', icon: Clock }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading disbursements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Driver Disbursements</h2>
          <p className="text-gray-600">Manage mobile money payments to drivers</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-green-600 hover:bg-green-700"
        >
          <Send className="w-4 h-4 mr-2" />
          New Disbursement
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Disbursed</p>
                <p className="text-2xl font-bold text-gray-900">K{stats.total_amount.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_disbursements}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Average Amount</p>
                <p className="text-2xl font-bold text-gray-900">K{stats.average_amount.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Eligible Drivers</p>
                <p className="text-2xl font-bold text-gray-900">{drivers.length}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* PawaPay weekly payout report */}
      <PawaPayWeeklyReport />

      {/* Disbursement Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">New Disbursement</h3>
                <p className="text-sm text-green-100">Send payment to driver's mobile money</p>
              </div>
              <Button
                onClick={() => { setShowModal(false); resetModal(); }}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white hover:bg-opacity-20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Select Driver */}
              <div>
                <Label>Select Driver</Label>
                <select
                  value={selectedDriver?.id || ''}
                  onChange={(e) => {
                    const driver = drivers.find(d => d.id === e.target.value);
                    setSelectedDriver(driver);
                    setAccountCheck(null);
                  }}
                  className="w-full mt-2 p-2 border rounded-lg"
                >
                  <option value="">Choose a driver...</option>
                  {drivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name} - K{(driver.pending_earnings || 0).toFixed(2)} pending
                    </option>
                  ))}
                </select>
              </div>

              {/* Driver Info */}
              {selectedDriver && (
                <Card className="p-4 bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                      {selectedDriver.first_name[0]}{selectedDriver.last_name[0]}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{selectedDriver.first_name} {selectedDriver.last_name}</p>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="w-3 h-3" />
                        <span>{selectedDriver.phone}</span>
                      </div>
                      <p className="text-sm text-green-600 font-semibold">
                        Pending: K{(selectedDriver.pending_earnings || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Mobile Money Provider */}
              <div>
                <Label>Mobile Money Provider</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Button
                    type="button"
                    variant={provider === 'airtel' ? 'default' : 'outline'}
                    className={provider === 'airtel' ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => { setProvider('airtel'); setAccountCheck(null); }}
                  >
                    Airtel Money
                  </Button>
                  <Button
                    type="button"
                    variant={provider === 'tnm' ? 'default' : 'outline'}
                    className={provider === 'tnm' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    onClick={() => { setProvider('tnm'); setAccountCheck(null); }}
                  >
                    TNM Mpamba
                  </Button>
                </div>
              </div>

              {/* Check Account */}
              {selectedDriver && (
                <div>
                  <Button
                    type="button"
                    onClick={handleCheckAccount}
                    disabled={checking}
                    variant="outline"
                    className="w-full"
                  >
                    {checking ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Checking Account...
                      </>
                    ) : (
                      <>
                        <Smartphone className="w-4 h-4 mr-2" />
                        Verify {provider.toUpperCase()} Account
                      </>
                    )}
                  </Button>

                  {accountCheck && (
                    <Card className={`mt-3 p-4 ${accountCheck.account_exists ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-start space-x-3">
                        {accountCheck.account_exists ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className={`font-semibold ${accountCheck.account_exists ? 'text-green-900' : 'text-red-900'}`}>
                            {accountCheck.account_exists ? 'Account Verified' : 'Account Not Found'}
                          </p>
                          {accountCheck.account_name && (
                            <p className="text-sm text-gray-700">Name: {accountCheck.account_name}</p>
                          )}
                          <p className="text-sm text-gray-700">Provider: {accountCheck.provider.toUpperCase()}</p>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {/* Amount */}
              <div>
                <Label>Amount (MWK)</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                  step="100"
                  className="mt-2"
                />
                {selectedDriver && amount && parseFloat(amount) > (selectedDriver.pending_earnings || 0) && (
                  <p className="text-xs text-red-600 mt-1">
                    Amount exceeds pending earnings (K{(selectedDriver.pending_earnings || 0).toFixed(2)})
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes..."
                  className="mt-2"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button
                  onClick={handleDisburse}
                  disabled={!selectedDriver || !amount || processing || !accountCheck?.account_exists}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {processing ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send K{amount || '0'}
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => { setShowModal(false); resetModal(); }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>

              {!accountCheck?.account_exists && accountCheck && (
                <Card className="p-3 bg-yellow-50 border-yellow-200">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> Please verify the mobile money account before proceeding with disbursement.
                  </p>
                </Card>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Recent Disbursements */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Disbursements</h3>
          
          {disbursements.length === 0 ? (
            <div className="text-center py-8">
              <Send className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No disbursements yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {disbursements.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{d.driver_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{d.phone_number}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">
                        K{d.amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <span className="uppercase font-medium">{d.provider}</span>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(d.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(d.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DisbursementsPage;
